/**
 * Getting the desktop app.
 *
 * WHY THE PANEL LEADS WITH ONE BUTTON
 *   A download page that asks an engineer to know their own CPU architecture
 *   is a download page that gets a support question. So we work out what they
 *   are on, lead with that one build, and put the others behind a quiet line.
 *
 * WHY IT MAY OFFER NO DOWNLOAD AT ALL
 *   Because nothing has been published, or because the server has not been
 *   given a token for the registry. A button pointing at a URL that 404s is
 *   worse than no button: it costs the person a click, a wait and a guess
 *   about whether they did something wrong. When there is nothing to download
 *   we say so and give the exact two commands that build one.
 *
 * DATA DEFINITIONS
 *
 *   An Os is "macos" | "windows" | "linux" | "unknown".
 *   An Arch is "arm64" | "x64" | "unknown".
 *   Interpretation: what the visitor is running. "unknown" is a real answer
 *   and the interface must read sensibly when it is the one we have — Safari
 *   on an Apple-silicon Mac reports an Intel user agent, on purpose, and no
 *   amount of sniffing will change that.
 *
 *   A Build is one artifact that HAS BEEN PUBLISHED: an operating system, an
 *   architecture, the file name, and how to say it to a person.
 *   Interpretation: read out of the release manifest, never guessed. This file
 *   used to carry the names as constants, and they were wrong — they were
 *   electron-builder's defaults, and packages/desktop/electron-builder.yml
 *   overrides artifactName, so every link pointed at a file that does not
 *   exist. A name that is derived twice is a name that can disagree with
 *   itself; the release says what it contains and we read it.
 *
 *   A ReleaseManifest is what `npm run dist` writes and CI uploads beside the
 *   artifacts: { version, builtAt, artifacts: [{file, platform, arch, kind,
 *   size, sha256}] }. See packages/desktop/scripts/release-manifest.mjs, which
 *   refuses to write one for a build it could not verify — so anything named
 *   in it is a real, checked artifact.
 */
import { useEffect, useState } from 'react'
import {
  MonitorDown, Apple, Terminal, Check, Copy, CircleCheck, ExternalLink,
  FolderSync, BellRing, Zap,
} from 'lucide-react'
import { Button, Tooltip, Separator } from '@/components/ui/primitives'
import { isDesktop } from '@/hooks/useDesktop'
import { cn } from '@/lib/utils'

export type Os = 'macos' | 'windows' | 'linux' | 'unknown'
export type Arch = 'arm64' | 'x64' | 'unknown'

export interface PlatformGuess {
  readonly os: Os
  readonly arch: Arch
}

export interface Build {
  readonly id: string
  readonly os: Os
  readonly arch: Arch
  readonly label: string
  readonly file: string
  /** Bytes, when the manifest said. Shown so nobody starts a 100MB download blind. */
  readonly size?: number
}

/** One entry in the published manifest, as it appears on the wire. */
export interface ManifestArtifact {
  readonly file: string
  readonly platform?: string
  readonly arch?: string
  readonly kind?: string
  readonly size?: number
  readonly sha256?: string
}

export interface ReleaseManifest {
  readonly version?: string
  readonly builtAt?: string
  readonly artifacts?: readonly ManifestArtifact[]
}

/* ------------------------------------------------------------------ */
/* Configuration                                                       */
/* ------------------------------------------------------------------ */

/**
 * Where the list of builds comes from, and where a build comes from.
 *
 * WHY THESE ARE OUR OWN API AND NOT THE REGISTRY
 *   The installers live in GitLab's generic package registry, which is private:
 *   an anonymous request gets a 404, and the token that would fix it cannot go
 *   anywhere near a browser. So the server asks, holding the token, behind the
 *   session cookie that already protects every other thing here — see
 *   packages/server/src/releases.ts. Same origin, so no CORS question either.
 *
 *   This file used to carry the file NAMES as constants, and they were wrong:
 *   they were electron-builder's defaults, and electron-builder.yml overrides
 *   artifactName, so every link pointed at a file that does not exist. Nothing
 *   here is guessed any more.
 */
const MANIFEST_URL = '/api/desktop/manifest'
const DOWNLOAD_BASE = '/api/desktop/download'

/** Which version to say before the manifest has been read. Only a fallback. */
const RELEASE_VERSION = '0.1.0'

/** The exact commands that produce a build, for when none is published. */
export const BUILD_COMMAND = 'npm install\nnpm run dist --workspace @lattice/desktop'

/** The manifest's vocabulary, which is the packaging script's, not ours. */
const OS_OF: Readonly<Record<string, Os>> = { mac: 'macos', win: 'windows', linux: 'linux' }

/** How to say an artifact out loud. Only the parts that tell builds apart. */
function labelFor(os: Os, arch: Arch, kind: string | undefined): string {
  if (os === 'macos') return arch === 'arm64' ? 'macOS · Apple silicon' : arch === 'x64' ? 'macOS · Intel' : 'macOS'
  if (os === 'windows') return arch === 'arm64' ? 'Windows · Arm' : 'Windows'
  if (os === 'linux') {
    const format = kind === 'deb' ? 'deb' : kind === 'AppImage' ? 'AppImage' : kind ?? ''
    const chip = arch === 'arm64' ? ' · Arm' : ''
    return `Linux · ${format}${chip}`.replace(' · ', ' · ')
  }
  return 'Desktop'
}

/**
 * The builds a manifest describes, in the order to offer them.
 *
 * Anything whose platform we do not recognise is dropped rather than shown
 * under a made-up name: an unlabelled button is worse than one fewer button.
 * buildsFromManifest : ReleaseManifest -> [Build]
 */
export function buildsFromManifest(manifest: ReleaseManifest | undefined): Build[] {
  const order: Os[] = ['macos', 'windows', 'linux']
  return (manifest?.artifacts ?? [])
    .flatMap(artifact => {
      const os = OS_OF[String(artifact.platform)]
      if (!os || typeof artifact.file !== 'string' || artifact.file.length === 0) return []
      const arch: Arch = artifact.arch === 'arm64' || artifact.arch === 'x64' ? artifact.arch : 'unknown'
      return [{
        id: `${os}-${arch}-${artifact.kind ?? 'app'}`,
        os,
        arch,
        label: labelFor(os, arch, artifact.kind),
        file: artifact.file,
        ...(typeof artifact.size === 'number' && artifact.size > 0 ? { size: artifact.size } : {}),
      }]
    })
    .sort((a, b) => order.indexOf(a.os) - order.indexOf(b.os) || a.label.localeCompare(b.label))
}

/** A Release is { version, builds } — what is downloadable, and what to call it. */
export interface Release {
  readonly version: string
  readonly builds: readonly Build[]
}

/** The ordinary state of a deployment with no release: not an error. */
const NOTHING_PUBLISHED: Release = { version: RELEASE_VERSION, builds: [] }

/**
 * Fetch the published manifest, or nothing.
 *
 * A failure here is not an error to report: it is the ordinary state of a
 * deployment with no release yet, and the panel already has something useful
 * to say in that case — the two commands that build one.
 * loadRelease : AbortSignal? -> Promise<Release>
 */
export async function loadRelease(signal?: AbortSignal): Promise<Release> {
  try {
    const response = await fetch(MANIFEST_URL, { signal, credentials: 'same-origin' })
    if (!response.ok) return NOTHING_PUBLISHED
    const manifest = await response.json() as ReleaseManifest
    return {
      // The release says its own version. The environment variable is only a
      // fallback for a deployment that has not been told where releases live,
      // and it is exactly the thing that goes stale.
      version: typeof manifest.version === 'string' && manifest.version ? manifest.version : RELEASE_VERSION,
      builds: buildsFromManifest(manifest),
    }
  } catch {
    return NOTHING_PUBLISHED
  }
}

/**
 * Where a build can be downloaded from.
 * Only ever a build the manifest named, so the name is one the server will
 * recognise; anything else 404s there rather than here.
 * releaseUrl : Build -> String
 */
export const releaseUrl = (build: Build): string =>
  `${DOWNLOAD_BASE}/${encodeURIComponent(build.file)}`

/** Roughly how big, for a person deciding whether to start it now. */
export const megabytes = (bytes: number | undefined): string | undefined =>
  typeof bytes === 'number' && bytes > 0 ? `${Math.round(bytes / 1_000_000)} MB` : undefined

/* ------------------------------------------------------------------ */
/* Which machine is this                                               */
/* ------------------------------------------------------------------ */

/**
 * Work out what the visitor is running, from whatever evidence there is.
 *
 * `architecture` comes from the User-Agent Client Hints API, which is the only
 * honest source: Chromium tells us "arm" outright, and every other browser
 * deliberately does not. A guess of "unknown" means we show both Mac builds
 * rather than picking one and being wrong half the time.
 * guessPlatform : {userAgent, platform?, architecture?} -> PlatformGuess
 */
export function guessPlatform(input: {
  userAgent?: string; platform?: string; architecture?: string
}): PlatformGuess {
  const ua = `${input.userAgent ?? ''} ${input.platform ?? ''}`
  const os: Os =
    /android/i.test(ua) ? 'unknown'
      : /mac|darwin/i.test(ua) ? 'macos'
        : /win/i.test(ua) ? 'windows'
          : /linux|x11|ubuntu/i.test(ua) ? 'linux'
            : 'unknown'

  const hint = (input.architecture ?? '').toLowerCase()
  const arch: Arch =
    hint.includes('arm') ? 'arm64'
      : hint.includes('86') || hint.includes('x64') ? 'x64'
        : /arm64|aarch64/i.test(ua) ? 'arm64'
          : /x86_64|x64|win64|wow64|amd64/i.test(ua) ? 'x64'
            : 'unknown'

  return { os, arch }
}

/**
 * The build to lead with, or undefined when we cannot tell what they are on.
 *
 * On a Mac of unknown architecture this answers Apple silicon: every Mac sold
 * since 2020 is one, and the panel says plainly which it picked and how to
 * check. Being usefully right and openly checkable beats a menu of four.
 * bestBuild : PlatformGuess -> Build|undefined
 */
export function bestBuild(guess: PlatformGuess, builds: readonly Build[]): Build | undefined {
  const forOs = builds.filter(build => build.os === guess.os)
  if (forOs.length === 0) return undefined
  return forOs.find(build => build.arch === guess.arch) ?? forOs[0]
}

/** Everything that is not the recommendation. otherBuilds : Build|undefined [Build] -> [Build] */
export const otherBuilds = (best: Build | undefined, builds: readonly Build[]): Build[] =>
  builds.filter(build => build.id !== best?.id)

/** True when we are guessing at the architecture rather than being told. */
export const archIsGuessed = (guess: PlatformGuess): boolean =>
  guess.os === 'macos' && guess.arch === 'unknown'

/* ------------------------------------------------------------------ */
/* The panel                                                           */
/* ------------------------------------------------------------------ */

/** What the shell adds over a browser tab, in the order people care. */
const BENEFITS: { icon: typeof FolderSync; title: string; detail: string }[] = [
  { icon: FolderSync, title: 'Folder sync', detail: 'Mirror a project into a folder and pull or push changes.' },
  { icon: BellRing, title: 'Notifications', detail: 'Native alerts when a colleague changes a file you follow.' },
  { icon: Zap, title: 'Deep links', detail: 'A notification opens the exact drawing it is about.' },
]

/**
 * The get-the-app panel.
 * Shown on the Changes screen in a browser, and anywhere else the integrator
 * wants it. Inside the shell it says so instead of offering a second copy —
 * unless `force`, which is for the one case where somebody already in the app
 * needs a NEWER one: a shell too old to have the feature they just asked for.
 * DownloadApp : {compact?, force?} -> Element
 */
export function DownloadApp({ compact, force }: { compact?: boolean; force?: boolean }) {
  const [guess, setGuess] = useState<PlatformGuess>(() => guessPlatform(readNavigator()))
  const [release, setRelease] = useState<Release>(NOTHING_PUBLISHED)

  // Client Hints are asynchronous, so the first paint uses the user agent and
  // this sharpens it a moment later. Nothing moves except one label.
  useEffect(() => {
    let live = true
    void highEntropyArchitecture().then(architecture => {
      if (live && architecture) setGuess(guessPlatform({ ...readNavigator(), architecture }))
    })
    return () => { live = false }
  }, [])

  // What has actually been published. Until this answers — and forever, on a
  // deployment with no release — the panel shows the build-it-yourself path,
  // which is true rather than merely quiet.
  useEffect(() => {
    // `live` as well as the abort: an aborted fetch still RESOLVES here, to
    // "nothing published", and under StrictMode's double mount that answer can
    // land after the second attempt's good one and blank the panel.
    let live = true
    const controller = new AbortController()
    void loadRelease(controller.signal).then(release => { if (live) setRelease(release) })
    return () => { live = false; controller.abort() }
  }, [])

  if (isDesktop() && !force) return <AlreadyInstalled />

  const best = bestBuild(guess, release.builds)
  const url = best && releaseUrl(best)

  return (
    <section
      aria-labelledby="download-app-title"
      className={cn(
        'rounded-[var(--radius-lg)] border border-[var(--border)] bg-raised shadow-card',
        compact ? 'p-3' : 'p-4',
      )}
    >
      <header className="flex items-start gap-2.5">
        <MonitorDown className="mt-0.5 h-5 w-5 shrink-0 text-[var(--accent)]" strokeWidth={1.5} />
        <div className="min-w-0">
          <h3 id="download-app-title" className="text-base font-semibold tracking-tight">Lattice for desktop</h3>
          <p className="text-sm text-[var(--fg-muted)]">
            The same application, in a window that can reach your filesystem.
          </p>
        </div>
      </header>

      {!compact && (
        <ul className="mt-3 grid gap-2 sm:grid-cols-3">
          {BENEFITS.map(benefit => (
            <li key={benefit.title} className="rounded-[var(--radius-md)] bg-inset px-2.5 py-2">
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <benefit.icon className="h-3.5 w-3.5 text-[var(--fg-muted)]" strokeWidth={1.75} />
                {benefit.title}
              </div>
              <p className="mt-0.5 text-xs text-[var(--fg-subtle)] leading-relaxed">{benefit.detail}</p>
            </li>
          ))}
        </ul>
      )}

      <Separator className="my-3" />

      {url && best
        ? <Published best={best} url={url} guess={guess} release={release} />
        : <BuildItYourself best={best} />}
    </section>
  )
}

/** The happy path: a real file to download. */
function Published(
  { best, url, guess, release }:
  { best: Build; url: string; guess: PlatformGuess; release: Release },
) {
  const size = megabytes(best.size)
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant="accent" size="lg">
          <a href={url} download>
            {best.os === 'macos' ? <Apple className="h-4 w-4" strokeWidth={1.75} /> : <MonitorDown className="h-4 w-4" strokeWidth={1.75} />}
            Download for {best.label}
          </a>
        </Button>
        <span className="tabular text-xs text-[var(--fg-subtle)]">
          v{release.version}{size ? ` · ${size}` : ''}
        </span>
      </div>

      {archIsGuessed(guess) && (
        <p className="text-xs text-[var(--fg-subtle)]">
          Your browser will not say which chip this Mac has, so we have assumed Apple silicon.
          Apple menu → About This Mac: “Apple M…” means Apple silicon, “Intel” means the other one.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--fg-subtle)]">
        <span>Other platforms:</span>
        {otherBuilds(best, release.builds).map(other => (
          <a
            key={other.id} href={releaseUrl(other)} download
            className="snap inline-flex items-center gap-1 text-[var(--fg-muted)] hover:text-[var(--accent)]"
          >
            {other.label}
            <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
          </a>
        ))}
      </div>
    </div>
  )
}

/**
 * The honest path: nothing is published, so here is how to make one.
 * Saying "not published yet" costs the reader nothing; a link that 404s costs
 * them a download, a puzzled minute and a message to whoever wrote this.
 */
function BuildItYourself({ best }: { best: Build | undefined }) {
  return (
    <div className="space-y-2">
      <p className="text-sm text-[var(--fg-muted)]">
        No desktop build has been published yet. You can make one from the repository in about two
        minutes — it takes one command, and the result is a normal installer.
      </p>
      <CommandBlock command={BUILD_COMMAND} />
      <p className="text-xs text-[var(--fg-subtle)]">
        The installer lands in <code className="font-mono">packages/desktop/release/</code>
        {best ? <> — on this machine that is <span className="text-[var(--fg-muted)]">{best.file}</span></> : null}.
        electron-builder builds for the machine it runs on, so run it on the platform you want.
        To just try it without packaging:{' '}
        <code className="font-mono">npm run dev --workspace @lattice/desktop</code>.
      </p>
    </div>
  )
}

/** A copyable command, because retyping a command from a screenshot is a tax. */
function CommandBlock({ command }: { command: string }) {
  const [copied, setCopied] = useState(false)

  const copy = (): void => {
    void navigator.clipboard?.writeText(command)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600) })
      .catch(() => undefined)
  }

  return (
    <div className="relative rounded-[var(--radius-md)] border border-[var(--border)] bg-inset">
      <pre className="overflow-x-auto px-2.5 py-2 pr-10 font-mono text-xs leading-relaxed text-[var(--fg)]">
        <code>{command}</code>
      </pre>
      <Tooltip label={copied ? 'Copied' : 'Copy'}>
        <Button
          variant="ghost" size="icon"
          className="absolute right-1 top-1"
          onClick={copy}
          aria-label="Copy the build command"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-[var(--added)]" strokeWidth={2} />
            : <Copy className="h-3.5 w-3.5" strokeWidth={1.75} />}
        </Button>
      </Tooltip>
    </div>
  )
}

/** Inside the shell there is nothing to download. */
function AlreadyInstalled() {
  const [version, setVersion] = useState<string>()

  useEffect(() => {
    let live = true
    void (globalThis as { lattice?: { platform(): Promise<{ platform: string; version: string }> } })
      .lattice?.platform()
      .then(info => { if (live) setVersion(`${info.platform} · Electron ${info.version}`) })
      .catch(() => undefined)
    return () => { live = false }
  }, [])

  return (
    <div className="flex items-center gap-2 rounded-[var(--radius-lg)] border border-[var(--border)] bg-raised px-3 py-2.5 shadow-card">
      <CircleCheck className="h-4 w-4 shrink-0 text-[var(--added)]" strokeWidth={1.75} />
      <div className="min-w-0">
        <p className="text-sm font-medium">You are running the desktop app</p>
        <p className="truncate text-xs text-[var(--fg-subtle)]">{version ?? 'There is nothing to download.'}</p>
      </div>
    </div>
  )
}

/**
 * A compact "get the app" button, for a toolbar rather than a page.
 * It links straight to the right build when one exists, and otherwise opens
 * whatever the caller uses to show the full panel.
 * DownloadAppButton : {onShowPanel?} -> Element
 */
export function DownloadAppButton({ onShowPanel }: { onShowPanel?: () => void }) {
  const guess = guessPlatform(readNavigator())
  const [release, setRelease] = useState<Release>(NOTHING_PUBLISHED)

  useEffect(() => {
    // `live` as well as the abort: an aborted fetch still RESOLVES here, to
    // "nothing published", and under StrictMode's double mount that answer can
    // land after the second attempt's good one and blank the panel.
    let live = true
    const controller = new AbortController()
    void loadRelease(controller.signal).then(release => { if (live) setRelease(release) })
    return () => { live = false; controller.abort() }
  }, [])

  const best = bestBuild(guess, release.builds)
  const url = best && releaseUrl(best)

  if (isDesktop()) return null

  if (url) {
    return (
      <Tooltip label={`Download Lattice for ${best?.label}`}>
        <Button asChild variant="secondary" size="sm">
          <a href={url} download>
            <MonitorDown className="h-3.5 w-3.5" strokeWidth={1.75} /> Desktop app
          </a>
        </Button>
      </Tooltip>
    )
  }

  return (
    <Tooltip label="How to get the desktop app">
      <Button variant="secondary" size="sm" onClick={onShowPanel}>
        <Terminal className="h-3.5 w-3.5" strokeWidth={1.75} /> Desktop app
      </Button>
    </Tooltip>
  )
}

/** What this browser will admit to, guarded for a non-browser environment. */
function readNavigator(): { userAgent?: string; platform?: string } {
  if (typeof navigator === 'undefined') return {}
  return { userAgent: navigator.userAgent, platform: navigator.platform }
}

/**
 * Ask Chromium what CPU this is. Resolves undefined everywhere else.
 * highEntropyArchitecture : -> Promise<String|undefined>
 */
async function highEntropyArchitecture(): Promise<string | undefined> {
  const data = (navigator as Navigator & {
    userAgentData?: { getHighEntropyValues?(hints: string[]): Promise<{ architecture?: string }> }
  }).userAgentData
  if (!data?.getHighEntropyValues) return undefined
  try {
    return (await data.getHighEntropyValues(['architecture'])).architecture
  } catch {
    // Some browsers reject the request outright; an unknown architecture is
    // already a state this panel handles.
    return undefined
  }
}
